/**
 * Per-page view state persisted in sessionStorage.
 *
 * Campaigns, Folders, Pacing, Campaign QA and Creative Manager each remember
 * where you were — page number, page size, sort, search term, folder filter —
 * under a `<page>_paginationState` key so a round trip to a detail page and
 * back doesn't dump you at the top of page 1.
 *
 * The catch: those keys are scoped to the PAGE, not to the account. Switching
 * accounts therefore carried the previous account's view into the new one, and
 * a page number is the part that breaks visibly — sitting on page 4 of a large
 * account and switching to one with five folders asks the API for rows 31-40 of
 * 5. The backend clamps only the LOWER bound (Math.max(1, page)), so it answers
 * honestly with an empty list and `currentPage: 4, totalPages: 1`, and the page
 * renders "No folders yet" beside a count badge reading 5.
 *
 * Clearing on an account switch is the fix at the cause. Pages also recover
 * from an out-of-range page on their own (see the page-overflow recovery in
 * CampaignsPage/FoldersPage's fetch), which covers state that went stale
 * WITHIN one account — deleting rows until the last page no longer exists.
 */
const PAGE_VIEW_STATE_SUFFIX = '_paginationState';

/**
 * Drop every page's remembered view state.
 *
 * Matches on the shared suffix rather than a hard-coded list so a page added
 * later is covered without anyone having to remember this file exists.
 *
 * @returns {string[]} the keys that were cleared (for logging/tests)
 */
export function clearPersistedPageViewState() {
  try {
    // Collect first, remove second: removing during the scan reindexes
    // sessionStorage underneath the loop and skips entries.
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key && key.endsWith(PAGE_VIEW_STATE_SUFFIX)) keys.push(key);
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
    return keys;
  } catch (e) {
    // Storage disabled (private mode) or unavailable. Nothing to clear, and an
    // account switch must never fail over view state.
    console.warn('Could not clear persisted page view state', e);
    return [];
  }
}

export default { clearPersistedPageViewState };
